/**
 * ============================================================================
 * BLACK HOLE SIMULATION WITH RAYMARCHED GRAVITATIONAL LENSING
 * ============================================================================
 *
 * This simulation renders a Schwarzschild (non-rotating) black hole with:
 * - Gravitational lensing of light rays through curved spacetime
 * - Accretion disk with temperature-based coloring and turbulence
 * - Doppler beaming (relativistic brightness variation)
 * - Procedural star field and nebula background
 * - Photon ring at the critical impact parameter
 *
 * The code is organized pedagogically for use in a blog post explaining
 * the physics and implementation of black hole rendering.
 *
 * @author Daniel Greenheck
 * @see BLOG.md for detailed explanation
 */

import * as THREE from "three/webgpu";
import { uniform } from "three/tsl";
import { createBlackHoleShader } from "./blackhole-shader.js";

const MAX_CONNECTOR_COUNT = 8;

// ============================================================================
// SECTION 1: BLACK HOLE SIMULATION CLASS
// ============================================================================

export class BlackHoleSimulation {
  constructor(scene, config) {
    this.scene = scene;
    this.config = config;
    this.blackHoleMesh = null;
    this.cameraDirection = new THREE.Vector3(0, 0, -1);
    this.cameraRight = new THREE.Vector3(1, 0, 0);
    this.cameraUp = new THREE.Vector3(0, 1, 0);
    this.lastCameraFov = Number.NaN;
    this.lastCameraAspect = Number.NaN;
    this.initializeUniforms(config);
  }

  /**
   * Initialize all shader uniforms with default values.
   * These can be updated in real-time via the UI.
   */
  initializeUniforms(config) {
    const connectorUniforms = Object.fromEntries(
      Array.from({ length: MAX_CONNECTOR_COUNT }, (_, index) => {
        const slot = index + 1;
        return [
          [`connector${slot}Enabled`, uniform(0.0)],
          [`connector${slot}Start`, uniform(new THREE.Vector2(0, 0))],
          [`connector${slot}P1`, uniform(new THREE.Vector2(0, 0))],
          [`connector${slot}P2`, uniform(new THREE.Vector2(0, 0))],
          [`connector${slot}P3`, uniform(new THREE.Vector2(0, 0))],
          [`connector${slot}End`, uniform(new THREE.Vector2(0, 0))],
          [`connector${slot}EmergenceStart`, uniform(0.0)],
          [`connector${slot}EmergenceEnd`, uniform(0.0)],
          [`connector${slot}Thickness`, uniform(config.connectorWidth ?? 0.22)],
          [`connector${slot}Opacity`, uniform(config.connectorOpacity ?? 0.14)],
          [`connector${slot}Brightness`, uniform(config.connectorBrightness ?? 1.16)],
        ];
      }).flat(),
    );

    this.uniforms = {
      // === Physics ===
      blackHoleMass: uniform(config.blackHoleMass ?? 1.0),

      // === Accretion Disk Geometry ===
      // Inner radius constrained to ISCO (Innermost Stable Circular Orbit)
      // For Schwarzschild black hole: ISCO = 3 × rs (where rs = 2M = 2.0 in our units)
      diskInnerRadius: uniform(config.diskInnerRadius ?? 3.0),
      diskOuterRadius: uniform(config.diskOuterRadius ?? 12.0),

      // === Accretion Disk Appearance ===
      // Peak temperature in thousands of Kelvin (at inner edge)
      // Typical values: 5-50 (5,000K - 50,000K)
      diskTemperature: uniform(config.diskTemperature ?? 10.0),
      // Temperature falloff exponent: 0.75 = physical (Shakura-Sunyaev), higher = steeper
      temperatureFalloff: uniform(config.temperatureFalloff ?? 0.75),
      diskBrightness: uniform(config.diskBrightness ?? 2.0),
      diskRotationSpeed: uniform(config.diskRotationSpeed ?? 0.3),

      // === Turbulence Pattern Controls ===
      turbulenceScale: uniform(config.turbulenceScale ?? 1.0),
      turbulenceStretch: uniform(config.turbulenceStretch ?? 5.0),
      turbulenceSharpness: uniform(config.turbulenceSharpness ?? 1.0),
      turbulenceCycleTime: uniform(config.turbulenceCycleTime ?? 10.0),
      turbulenceLacunarity: uniform(config.turbulenceLacunarity ?? 2.0),
      turbulencePersistence: uniform(config.turbulencePersistence ?? 0.5),

      // === Disk Edge Falloff ===
      diskEdgeSoftnessInner: uniform(config.diskEdgeSoftnessInner ?? 0.15),
      diskEdgeSoftnessOuter: uniform(config.diskEdgeSoftnessOuter ?? 0.15),

      // === Relativistic Effects ===
      gravitationalLensing: uniform(config.gravitationalLensing ?? 1.5),
      dopplerStrength: uniform(config.dopplerStrength ?? 1.0),

      // === Performance ===
      stepSize: uniform(config.stepSize ?? 0.3),
      // Rays only need expensive GR integration once they enter the local
      // singularity field. This keeps portrait/mobile overview cameras free to
      // move well beyond the original 100-unit demo envelope.
      raymarchEntryRadius: uniform(config.raymarchEntryRadius ?? 52.0),

      // === Stars ===
      starsEnabled: uniform(config.starsEnabled ? 1.0 : 0.0),
      starBackgroundColor: uniform(new THREE.Color(config.starBackgroundColor ?? "#000000")),
      starDensity: uniform(config.starDensity ?? 0.003),
      starSize: uniform(config.starSize ?? 2.0),
      starBrightness: uniform(config.starBrightness ?? 1.0),

      // === Nebula Layer 1 ===
      nebulaEnabled: uniform(config.nebulaEnabled ? 1.0 : 0.0),
      nebula1Scale: uniform(config.nebula1Scale ?? 2.0),
      nebula1Density: uniform(config.nebula1Density ?? 0.5),
      nebula1Brightness: uniform(config.nebula1Brightness ?? 0.15),
      nebula1Color: uniform(new THREE.Color(config.nebula1Color ?? "#1a0033")),

      // === Nebula Layer 2 ===
      nebula2Scale: uniform(config.nebula2Scale ?? 6.0),
      nebula2Density: uniform(config.nebula2Density ?? 0.5),
      nebula2Brightness: uniform(config.nebula2Brightness ?? 0.15),
      nebula2Color: uniform(new THREE.Color(config.nebula2Color ?? "#4d1a26")),

      // === Animation State ===
      time: uniform(0),

      // === Camera ===
      cameraPosition: uniform(new THREE.Vector3(0, 5, 20)),
      cameraForward: uniform(new THREE.Vector3(0, 0, -1)),
      cameraRight: uniform(new THREE.Vector3(1, 0, 0)),
      cameraUp: uniform(new THREE.Vector3(0, 1, 0)),
      cameraTanHalfFov: uniform(Math.tan(THREE.MathUtils.degToRad(30))),
      cameraAspect: uniform(window.innerWidth / Math.max(window.innerHeight, 1)),

      // === Raymarched connector filaments ===
      connectorWidth: uniform(config.connectorWidth ?? 0.22),
      connectorOpacity: uniform(config.connectorOpacity ?? 0.14),
      connectorBrightness: uniform(config.connectorBrightness ?? 1.16),
      connectorColor: uniform(new THREE.Color(config.connectorColor ?? "#b7c6e8")),
      connectorInnerRadius: uniform(1e6),

      ...connectorUniforms,
    };
  }

  /**
   * Create the black hole visualization mesh.
   * Uses a large inverted sphere as the render surface with a custom TSL shader.
   */
  createBlackHole() {
    // Clean up existing mesh
    if (this.blackHoleMesh) {
      this.scene.remove(this.blackHoleMesh);
      this.blackHoleMesh.material?.dispose();
      this.blackHoleMesh.geometry?.dispose();
    }

    // Create inverted sphere geometry (renders from inside)
    const geometry = new THREE.SphereGeometry(this.config.renderShellRadius ?? 256, 32, 32);
    geometry.scale(-1, 1, 1);

    // Create material with our raymarching shader
    const material = new THREE.MeshBasicNodeMaterial();
    material.colorNode = this.createRaymarchingShader();

    this.blackHoleMesh = new THREE.Mesh(geometry, material);
    this.blackHoleMesh.frustumCulled = false;
    this.scene.add(this.blackHoleMesh);
  }

  /**
   * Main shader creation method.
   * Builds the complete raymarching shader using Three.js TSL.
   */
  createRaymarchingShader() {
    return createBlackHoleShader(this.uniforms);
  }

  // ==========================================================================
  // SECTION 7: PUBLIC API
  // ==========================================================================

  /**
   * Update uniform values from config object.
   * Called when UI controls change.
   */
  updateUniforms(config) {
    const u = this.uniforms;

    // Physics
    if (config.blackHoleMass !== undefined) u.blackHoleMass.value = config.blackHoleMass;

    // Disk geometry
    if (config.diskInnerRadius !== undefined) u.diskInnerRadius.value = config.diskInnerRadius;
    if (config.diskOuterRadius !== undefined) u.diskOuterRadius.value = config.diskOuterRadius;

    // Disk appearance
    if (config.diskTemperature !== undefined) u.diskTemperature.value = config.diskTemperature;
    if (config.temperatureFalloff !== undefined) u.temperatureFalloff.value = config.temperatureFalloff;
    if (config.diskBrightness !== undefined) u.diskBrightness.value = config.diskBrightness;
    if (config.diskRotationSpeed !== undefined) u.diskRotationSpeed.value = config.diskRotationSpeed;

    // Turbulence pattern
    if (config.turbulenceScale !== undefined) u.turbulenceScale.value = config.turbulenceScale;
    if (config.turbulenceStretch !== undefined) u.turbulenceStretch.value = config.turbulenceStretch;
    if (config.turbulenceSharpness !== undefined) u.turbulenceSharpness.value = config.turbulenceSharpness;
    if (config.turbulenceCycleTime !== undefined) u.turbulenceCycleTime.value = config.turbulenceCycleTime;
    if (config.turbulenceLacunarity !== undefined) u.turbulenceLacunarity.value = config.turbulenceLacunarity;
    if (config.turbulencePersistence !== undefined) u.turbulencePersistence.value = config.turbulencePersistence;

    // Disk edge falloff
    if (config.diskEdgeSoftnessInner !== undefined) u.diskEdgeSoftnessInner.value = config.diskEdgeSoftnessInner;
    if (config.diskEdgeSoftnessOuter !== undefined) u.diskEdgeSoftnessOuter.value = config.diskEdgeSoftnessOuter;

    // Relativistic effects
    if (config.gravitationalLensing !== undefined) u.gravitationalLensing.value = config.gravitationalLensing;
    if (config.dopplerStrength !== undefined) u.dopplerStrength.value = config.dopplerStrength;

    // Performance
    if (config.stepSize !== undefined) u.stepSize.value = config.stepSize;
    if (config.raymarchEntryRadius !== undefined) u.raymarchEntryRadius.value = config.raymarchEntryRadius;

    // Star uniforms
    if (config.starsEnabled !== undefined) u.starsEnabled.value = config.starsEnabled ? 1.0 : 0.0;
    if (config.starBackgroundColor !== undefined) u.starBackgroundColor.value.set(config.starBackgroundColor);
    if (config.starDensity !== undefined) u.starDensity.value = config.starDensity;
    if (config.starSize !== undefined) u.starSize.value = config.starSize;
    if (config.starBrightness !== undefined) u.starBrightness.value = config.starBrightness;

    // Nebula Layer 1 uniforms
    if (config.nebulaEnabled !== undefined) u.nebulaEnabled.value = config.nebulaEnabled ? 1.0 : 0.0;
    if (config.nebula1Scale !== undefined) u.nebula1Scale.value = config.nebula1Scale;
    if (config.nebula1Density !== undefined) u.nebula1Density.value = config.nebula1Density;
    if (config.nebula1Brightness !== undefined) u.nebula1Brightness.value = config.nebula1Brightness;
    if (config.nebula1Color !== undefined) u.nebula1Color.value.set(config.nebula1Color);

    // Nebula Layer 2 uniforms
    if (config.nebula2Scale !== undefined) u.nebula2Scale.value = config.nebula2Scale;
    if (config.nebula2Density !== undefined) u.nebula2Density.value = config.nebula2Density;
    if (config.nebula2Brightness !== undefined) u.nebula2Brightness.value = config.nebula2Brightness;
    if (config.nebula2Color !== undefined) u.nebula2Color.value.set(config.nebula2Color);

    // Note: Disk color is computed from blackbody radiation (no color uniforms needed)

    if (config.connectorWidth !== undefined) u.connectorWidth.value = config.connectorWidth;
    if (config.connectorOpacity !== undefined) u.connectorOpacity.value = config.connectorOpacity;
    if (config.connectorBrightness !== undefined) u.connectorBrightness.value = config.connectorBrightness;
    if (config.connectorColor !== undefined) u.connectorColor.value.set(config.connectorColor);
  }

  setFieldGeometry(field = {}) {
    const connectors = field.connectors ?? [];
    let connectorInnerRadius = 1e6;
    for (let index = 0; index < MAX_CONNECTOR_COUNT; index += 1) {
      const slot = index + 1;
      const item = connectors[index];
      this.uniforms[`connector${slot}Enabled`].value = Number(item !== undefined);
      if (!item) continue;

      const startX = item.start[0];
      const startY = item.start[1];
      const endX = item.end[0];
      const endY = item.end[1];
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const deltaLength = Math.hypot(deltaX, deltaY) || 1;
      const curvature = item.curvature ?? 0;
      const controlX = (startX + endX) * 0.5 + (-deltaY / deltaLength) * curvature;
      const controlY = (startY + endY) * 0.5 + (deltaX / deltaLength) * curvature;
      const setBezierPoint = (target, t) => {
        const oneMinusT = 1 - t;
        target.set(
          startX * oneMinusT * oneMinusT + controlX * 2 * oneMinusT * t + endX * t * t,
          startY * oneMinusT * oneMinusT + controlY * 2 * oneMinusT * t + endY * t * t,
        );
      };
      const startRadius = Math.hypot(startX, startY);
      const emergenceStart = startRadius + 3.6;
      const emergenceEnd = startRadius + 9.8;
      connectorInnerRadius = Math.min(connectorInnerRadius, emergenceStart);

      this.uniforms[`connector${slot}Start`].value.set(startX, startY);
      setBezierPoint(this.uniforms[`connector${slot}P1`].value, 0.25);
      setBezierPoint(this.uniforms[`connector${slot}P2`].value, 0.5);
      setBezierPoint(this.uniforms[`connector${slot}P3`].value, 0.75);
      this.uniforms[`connector${slot}End`].value.set(endX, endY);
      this.uniforms[`connector${slot}EmergenceStart`].value = emergenceStart;
      this.uniforms[`connector${slot}EmergenceEnd`].value = emergenceEnd;
      this.uniforms[`connector${slot}Thickness`].value = item.thickness ?? this.uniforms.connectorWidth.value;
      this.uniforms[`connector${slot}Opacity`].value = item.opacity ?? this.uniforms.connectorOpacity.value;
      this.uniforms[`connector${slot}Brightness`].value = item.brightness ?? this.uniforms.connectorBrightness.value;
    }
    this.uniforms.connectorInnerRadius.value = connectorInnerRadius;
  }

  /**
   * Update camera position uniform from Three.js camera.
   */
  updateCamera(camera) {
    const u = this.uniforms;
    u.cameraPosition.value.copy(camera.position);

    // Quaternion rotation preserves these unit basis vectors; re-normalizing them
    // here and again per fragment is redundant work.
    this.cameraDirection.set(0, 0, -1).applyQuaternion(camera.quaternion);
    this.cameraRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
    this.cameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
    u.cameraForward.value.copy(this.cameraDirection);
    u.cameraRight.value.copy(this.cameraRight);
    u.cameraUp.value.copy(this.cameraUp);

    if (camera.fov !== this.lastCameraFov) {
      this.lastCameraFov = camera.fov;
      u.cameraTanHalfFov.value = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5));
    }
    if (camera.aspect !== this.lastCameraAspect) {
      this.lastCameraAspect = camera.aspect;
      u.cameraAspect.value = camera.aspect;
    }
  }

  /**
   * Main update method - called each frame.
   */
  update(deltaTime, camera) {
    this.uniforms.time.value += deltaTime;
    this.updateCamera(camera);
  }

  /**
   * Handle window resize.
   */
  onResize(width, height) {
    if (height > 0) this.uniforms.cameraAspect.value = width / height;
  }

  /**
   * Explicitly dispose the simulation-owned mesh resources.
   */
  dispose() {
    if (!this.blackHoleMesh) return;
    this.scene.remove(this.blackHoleMesh);
    this.blackHoleMesh.material?.dispose();
    this.blackHoleMesh.geometry?.dispose();
    this.blackHoleMesh = null;
  }

  /**
   * Regenerate the black hole mesh (e.g., after config changes).
   */
  regenerate() {
    this.createBlackHole();
  }
}
